import React, { useState, useEffect } from 'react';
import { Typography, Box, Dialog, DialogContent, IconButton, FormControl, InputLabel, Select, MenuItem, Button, SelectChangeEvent, TextField, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DePINHeader, { NetworkType } from '../../components/depin/DePINHeader';
import { useApi } from '../../contexts/ApiContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useWithTokenRefresh } from '../../utils/api';
import { toastError, toastSuccess } from '../../utils/toast';
import { ConnectButton } from '@mysten/dapp-kit';

// Unified wallet context
import { useUnifiedWeb3Wallet, WalletType } from '../../contexts/unified_wallet/UnifiedWeb3WalletContext';
import { BucketMarketplace } from '@flashbacktech/flashbackclient/stellarv2';

// SUI-specific imports
import { useSuiWallet } from '../../contexts/sui/SuiWalletContext';
import { Transaction as SuiTransaction, Commands } from '@mysten/sui/transactions';
import { ApiTypes, HttpError } from '@flashbacktech/flashbackclient/api';
import DePINHubTutorialLauncher from '../../components/depin/DePINHubTutorialLauncher';

// Unified bucket type for marketplace display
interface MarketplaceBucket {
  bucket_id: number | string;
  name: string;
  region: string;
  country?: string;
  provider_id: string;
  fb_bucket_id: string;
  price_per_gb_storage: bigint;
  price_per_gb_egress: bigint;
  api_compatibility: string;
  sla_avg_latency_ms: number;
  sla_avg_uptime_pct: number;
  status: string;
}

// Create Deal Modal Props
interface CreateDealModalProps {
  open: boolean;
  onClose: () => void;
  bucket: MarketplaceBucket | null;
  network: NetworkType;
  onDealCreated: () => void;
  suiClient?: any; // SUI client for blockchain interactions
}

// Create Deal Modal Component
const CreateDealModal: React.FC<CreateDealModalProps> = ({
  open,
  onClose,
  bucket,
  network,
  onDealCreated,
  suiClient,
}) => {
  const { walletAddress: unifiedWalletAddress, isConnected: unifiedIsConnected, suiSignAndExecute, stellarClient } = useUnifiedWeb3Wallet();
  const { isAuthenticated, globalData } = useAuth();
  const selectedWorkspaceId = globalData?.selectedWorkspaceId || '';
  const withTokenRefresh = useWithTokenRefresh();
  const { apiClient } = useApi();

  const [apiType, setApiType] = useState<string>('S3');
  const [capacityGb, setCapacityGb] = useState<number>(10);
  const [durationDays, setDurationDays] = useState<number>(30);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dealName, setDealName] = useState('');

  const walletAddress = unifiedWalletAddress;

  useEffect(() => {
    if (open) setDealName('');
  }, [open]);

  const handleApiTypeChange = (event: SelectChangeEvent) => {
    setApiType(event.target.value);
  };

  const handleCreateDeal = async () => {
    if (!walletAddress || !bucket) {
      toastError('Wallet not connected or storage unit not selected');
      return;
    }
    if (!dealName.trim()) {
      toastError('Please enter a deal name');
      return;
    }

    setIsSubmitting(true);
    let createStorageRepoResponse: ApiTypes.CreateRepoResponse | null = null;
    try {
      const createStorageRepo: ApiTypes.CreateRepoRequest = {
        name: dealName.trim(),
        storageType: ApiTypes.StorageType.S3,
        mode: ApiTypes.ModeType.NORMAL,
        workspaceId: selectedWorkspaceId,
        walletAddress,
        repoUnits: [
          {
            bucketId: bucket.fb_bucket_id,
            folder: '',
            master: true,
          } as ApiTypes.RepoUnitInfo
        ]
      };

      createStorageRepoResponse = await withTokenRefresh(() => apiClient.createStorageRepo(createStorageRepo));
      if (!createStorageRepoResponse.success || !createStorageRepoResponse.repoId) {
        toastError('Failed to create storage repo');
        setIsSubmitting(false);
        return;
      }
    } catch (error) {
      console.error('Failed to create deal:', error);
      if (error instanceof HttpError) {
        const body = error.data as { error_message?: string; error_code?: string } | undefined;
        const message = body?.error_message || error.message;
        toastError(message || 'Failed to create deal. Please try again.');
      } else {
        toastError(error instanceof Error ? error.message : 'Failed to create deal. Please try again.');
      }
      setIsSubmitting(false);
      return;
    }

    if (!createStorageRepoResponse) {
      setIsSubmitting(false);
      return;
    }

    try {
      if (network === 'stellar') {
        console.log('createDealParams', {
          consumer_id: walletAddress,
          provider_id: bucket.provider_id,
          bucket_id: typeof bucket.bucket_id === 'number' ? bucket.bucket_id : parseInt(bucket.bucket_id as string),
          fb_repo_id: createStorageRepoResponse.repoId.toString(),
          duration_secs: BigInt(durationDays) * BigInt(24 * 60 * 60), // duration must be > 0 (contract validation)
          agreed_storage_mb: capacityGb * 1024,
          agreed_egress_mb: 0,
          api_compatibility: apiType,
        });
        // Stellar: Create deal on smart contract
        if (!stellarClient || !createStorageRepoResponse) {
          throw new Error('Stellar client not available');
        }
        console.log('createDealParams', {
          consumer_id: walletAddress,
          provider_id: bucket.provider_id,
          bucket_id: typeof bucket.bucket_id === 'number' ? bucket.bucket_id : parseInt(bucket.bucket_id as string),
          fb_repo_id: createStorageRepoResponse.repoId.toString(),
          duration_secs: BigInt(durationDays) * BigInt(24 * 60 * 60), // duration must be > 0 (contract validation)
          agreed_storage_mb: capacityGb * 1024,
          agreed_egress_mb: 0,
          api_compatibility: apiType,
        });
        const dealId = await stellarClient.deals.createDeal(
          walletAddress,
          bucket.provider_id,
          typeof bucket.bucket_id === 'number' ? bucket.bucket_id : parseInt(bucket.bucket_id as string),
          createStorageRepoResponse.repoId.toString(),
          {
            duration_secs: BigInt(durationDays) * BigInt(24 * 60 * 60), // duration must be > 0 (contract validation)
            agreed_storage_mb: capacityGb * 1024,
            agreed_egress_mb: 0,
            api_compatibility: apiType,
          }
        );

        toastSuccess(`Deal created successfully!`);
      } else if (network === 'sui') {
        // SUI: Create deal on smart contract
        if (!suiClient) {
          throw new Error('SUI client not available');
        }

        // Fetch the bucket object to get the provider_id (which is the provider's address)
        const bucketObject = await suiClient.getObject({
          id: bucket.bucket_id as string,
          options: { showContent: true }
        });

        if (!bucketObject.data || !bucketObject.data.content || bucketObject.data.content.dataType !== 'moveObject') {
          throw new Error('Failed to fetch bucket object');
        }

        const bucketFields = (bucketObject.data.content as any).fields;
        const providerAddress = bucketFields.provider_id as string;

        // Query ProviderRegistry to get the Provider object ID
        const contractAddress = process.env.REACT_APP_SUI_PACKAGE_ID || '';
        
        // Get all ProviderRegistry objects (there should be one shared registry)
        const registryObjects = await suiClient.getOwnedObjects({
          filter: {
            StructType: `${contractAddress}::provider::ProviderRegistry`
          },
          options: { showContent: true }
        });

        if (registryObjects.data.length === 0) {
          throw new Error('ProviderRegistry not found');
        }

        const registryId = registryObjects.data[0].data?.objectId;
        if (!registryId) {
          throw new Error('Invalid ProviderRegistry');
        }

        // Get the registry object with dynamic fields to access the providers table
        const registry = await suiClient.getObject({
          id: registryId,
          options: { 
            showContent: true,
            showOwner: true
          }
        });

        // Get the Provider ID from the registry's providers table
        const registryFields = (registry.data?.content as any)?.fields;
        const providersTableId = registryFields?.providers?.fields?.id?.id;

        if (!providersTableId) {
          throw new Error('Providers table not found in registry');
        }

        // Query the dynamic field for this provider's address
        const providerIdField = await suiClient.getDynamicFieldObject({
          parentId: providersTableId,
          name: {
            type: 'address',
            value: providerAddress
          }
        });

        const providerObjectId = (providerIdField.data?.content as any)?.fields?.value;
        
        if (!providerObjectId) {
          throw new Error('Provider not found in registry. The provider may not be registered yet.');
        }

        // Create transaction
        const tx = new SuiTransaction();

        // Set gas budget explicitly to avoid automatic budget calculation issues
        tx.setGasBudget(100000000); // 0.1 SUI

        // Calculate payment: capacity * duration * price
        // Assuming price_per_gb_storage is per month (30 days)
        const paymentAmount = (BigInt(capacityGb) * BigInt(durationDays) * bucket.price_per_gb_storage) / BigInt(30);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - splitCoins accepts gas and amount parameters
        const [coin] = tx.splitCoins(tx.gas, [paymentAmount]);

        tx.add(Commands.MoveCall({
          target: `${contractAddress}::deal::create_deal`,
          arguments: [
            tx.object(providerObjectId),              // provider object
            coin,                                     // payment
            tx.pure.u64(capacityGb),                  // capacity_gb
            tx.pure.u64(durationDays),                // duration_days
            tx.pure.vector('u8', []),                 // walrus_metadata_blob_id need to be added from Nami Cloud dynamically
          ],
        }));

        // Use unified context's suiSignAndExecute which handles connection properly
        await suiSignAndExecute(tx);
        toastSuccess('SUI Deal creation initiated successfully!');
      }
      
      onDealCreated();
      onClose();
    } catch (error) {
      console.error('Failed to create deal in smart contract:', error);
      toastError(error instanceof Error ? error.message : 'Failed to create deal in smart contract');
      if (createStorageRepoResponse) {
        await withTokenRefresh(() => apiClient.deleteStorageRepo(createStorageRepoResponse!.repoId));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '13px',
      '& fieldset': { borderColor: '#D0D5DD' },
      '&:hover fieldset': { borderColor: '#98A2B3' },
      '&.Mui-focused fieldset': { borderColor: '#617BFF', borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root': { fontSize: '13px' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#617BFF' },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', border: '1px solid #E9EAEB', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #E9EAEB' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
              </div>
              <div>
                <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#181D27', lineHeight: '24px' }}>Create Deal</Typography>
                <Typography sx={{ fontSize: '13px', color: '#717680', lineHeight: '18px' }}>Set up a new storage deal with this provider</Typography>
              </div>
            </div>
            <IconButton onClick={onClose} size="small" sx={{ color: '#98A2B3', '&:hover': { color: '#535862', backgroundColor: '#F3F4F6' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '20px 24px 24px' }}>
          {/* Selected bucket info */}
          {bucket && (
            <div style={{ padding: '14px 16px', backgroundColor: '#FAFBFC', border: '1px solid #E9EAEB', borderRadius: 8, marginBottom: 20 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#98A2B3', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>Selected Storage Unit</Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#181D27' }}>{bucket.name}</Typography>
              <Typography sx={{ fontSize: '12px', color: '#717680', mt: 0.25 }}>{bucket.region} · Provider: {bucket.provider_id?.slice(0, 8)}...</Typography>
            </div>
          )}

          {/* Deal name */}
          <div style={{ marginBottom: 16 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#414651', mb: 0.75 }}>Deal Name *</Typography>
            <TextField
              fullWidth
              placeholder="e.g. My Storage Deal, Production Repo"
              value={dealName}
              onChange={(e) => setDealName(e.target.value)}
              disabled={isSubmitting}
              inputProps={{ maxLength: 128 }}
              size="small"
              sx={inputSx}
            />
          </div>

          {/* Capacity + Duration row */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#414651', mb: 0.75 }}>Capacity (GB)</Typography>
              <TextField
                fullWidth
                type="number"
                value={capacityGb}
                onChange={(e) => setCapacityGb(Number(e.target.value))}
                InputProps={{ inputProps: { min: 1 } }}
                size="small"
                sx={inputSx}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#414651', mb: 0.75 }}>Duration (Days)</Typography>
              <TextField
                fullWidth
                type="number"
                value={durationDays}
                onChange={(e) => setDurationDays(Number(e.target.value))}
                InputProps={{ inputProps: { min: 1 } }}
                size="small"
                sx={inputSx}
              />
            </div>
          </div>

          {/* API Type */}
          <div style={{ marginBottom: 20 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#414651', mb: 0.75 }}>API Type</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={apiType}
                onChange={handleApiTypeChange}
                sx={{
                  borderRadius: '8px',
                  fontSize: '13px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D5DD' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#98A2B3' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#617BFF', borderWidth: '1.5px' },
                }}
              >
                <MenuItem value="S3">AWS S3</MenuItem>
                <MenuItem value="GCS">Google Cloud Storage</MenuItem>
                <MenuItem value="BLOB">MS Azure Blob Storage</MenuItem>
              </Select>
            </FormControl>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              onClick={onClose}
              disabled={isSubmitting}
              sx={{ textTransform: 'none', fontWeight: 500, fontSize: '13px', color: '#717680', borderRadius: '8px', px: 2, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', color: '#535862' } }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateDeal}
              variant="contained"
              disabled={!dealName.trim() || isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '13px', borderRadius: '8px', px: 2.5, backgroundColor: '#617BFF', boxShadow: 'none', '&:hover': { backgroundColor: '#5A6FE8', boxShadow: 'none' } }}
            >
              {isSubmitting ? 'Creating...' : 'Initiate Deal'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function DePINHubMarketplace() {
  const { apiClient } = useApi();
  const { isAuthenticated, globalData } = useAuth();
  const selectedWorkspaceId = globalData?.selectedWorkspaceId || '';
  const navigate = useNavigate();
  const withTokenRefresh = useWithTokenRefresh();

  // Unified wallet context to detect network and connection status
  const { walletType, isConnected: unifiedIsConnected, walletAddress: unifiedWalletAddress, suiClient: unifiedSuiClient, stellarClient, connectWallet, loading: walletLoading } = useUnifiedWeb3Wallet();
  const network: NetworkType = walletType === WalletType.STELLAR ? 'stellar' : walletType === WalletType.SUI ? 'sui' : null;

  // SUI wallet context (for SUI-specific operations)
  const suiWallet = useSuiWallet();

  // Use unified SUI client if available, fallback to suiWallet client
  const suiClient = unifiedSuiClient || suiWallet.client;

  // Use unified connection status and wallet address
  const isConnected = unifiedIsConnected;
  const walletAddress = unifiedWalletAddress;

  // State
  const [buckets, setBuckets] = useState<MarketplaceBucket[]>([]);
  const [loading, setLoading] = useState(false);

  // Tutorial is always visible
  const showTutorial = true;
  const [searchQuery, setSearchQuery] = useState('');
  const [createDealModalOpen, setCreateDealModalOpen] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState<MarketplaceBucket | null>(null);

  // Stats
  const [stats, setStats] = useState({
    totalBuckets: 0,
    activeProviders: 0,
    avgReputation: 4.7,
    availableBuckets: 0,
  });

  // Build balances for header
  const getBalances = () => {
    if (network === 'stellar' && isConnected) {
      return [];
    } else if (network === 'sui' && isConnected) {
      return suiWallet.balance?.map(b => ({
        symbol: b.symbol,
        balance: (Number(b.balance) / Math.pow(10, b.decimals)).toFixed(2),
        color: b.symbol === 'SUI' ? '#4DA2FF' : '#10B981',
      })) || [];
    }
    return [];
  };

  // Load marketplace buckets — uses the same API pattern as DePINHubBuckets.loadBuckets
  const loadMarketplaceBuckets = async () => {
    if (!isConnected || !network) return;

    setLoading(true);
    try {
      let marketplaceBuckets: MarketplaceBucket[] = [];

      // Use the correct address prefix to filter DePIN buckets by network
      const addressPrefix = network === 'stellar' ? 'G' : '0x';

      // Load buckets from the Flashgate API (same pattern as DePINHubBuckets)
      const response = await withTokenRefresh(() =>
        apiClient.getStorageBuckets(selectedWorkspaceId || undefined, addressPrefix)
      );

      console.log('[Marketplace] API buckets response:', response?.buckets?.length, 'buckets');

      if (response?.buckets) {
        marketplaceBuckets = response.buckets.map(b => ({
          bucket_id: b.id,
          name: b.name,
          region: b.region || '',
          country: '',
          provider_id: (b as any).walletAddress || '',
          fb_bucket_id: b.id,
          price_per_gb_storage: BigInt(2000000),
          price_per_gb_egress: BigInt(1000000),
          api_compatibility: 'S3',
          sla_avg_latency_ms: 0,
          sla_avg_uptime_pct: 0,
          status: 'Active',
        }));
      }

      // For Stellar: also try to enrich with smart contract data
      if (network === 'stellar' && stellarClient && walletAddress) {
        try {
          const skip = 0;
          const itemsPerPage = 100;
          const contractBuckets = await stellarClient.buckets.getBuckets(walletAddress, skip, itemsPerPage);

          if (contractBuckets && contractBuckets.length > 0) {
            // Build a lookup from fb_bucket_id to contract data
            const contractMap = new Map<string, BucketMarketplace>();
            for (const cb of contractBuckets) {
              contractMap.set(cb.fb_bucket_id, cb);
            }
            console.log('!!contractMap', contractMap);
            // Enrich API buckets with contract data where available
            marketplaceBuckets = marketplaceBuckets.map(mb => {
              const cb = contractMap.get(mb.fb_bucket_id);
              if (cb) {
                return {
                  bucket_id: cb.bucket_id,
                  provider_id: cb.provider_id,
                  price_per_gb_storage: cb.price_per_gb_storage,
                  price_per_gb_egress: cb.price_per_gb_egress,
                  api_compatibility: cb.api_compatibility,
                  sla_avg_latency_ms: cb.sla_avg_latency_ms,
                  sla_avg_uptime_pct: cb.sla_avg_uptime_pct,
                  status: cb.status,
                  name: cb.name,
                  region: cb.region,
                  fb_bucket_id: cb.fb_bucket_id
                };
              }
              return mb;
            });

            console.log('!!marketplaceBuckets', marketplaceBuckets);

            // Add contract-only buckets not in API
            for (const cb of contractBuckets) {
              if (!marketplaceBuckets.some(mb => mb.fb_bucket_id === cb.fb_bucket_id)) {
                marketplaceBuckets.push({
                  bucket_id: cb.bucket_id,
                  name: cb.name,
                  region: cb.region,
                  country: '',
                  provider_id: cb.provider_id,
                  fb_bucket_id: cb.fb_bucket_id,
                  price_per_gb_storage: cb.price_per_gb_storage,
                  price_per_gb_egress: cb.price_per_gb_egress,
                  api_compatibility: cb.api_compatibility,
                  sla_avg_latency_ms: cb.sla_avg_latency_ms,
                  sla_avg_uptime_pct: cb.sla_avg_uptime_pct,
                  status: cb.status,
                });
              }
            }
          }
        } catch (contractError) {
          console.warn('[Marketplace] Could not load Stellar contract buckets:', contractError);
          // Continue with API-only data
        }
      }

      const totalBuckets = marketplaceBuckets.length;
      const availableBuckets = marketplaceBuckets.filter(b => b.status === 'Active').length;

      console.log('[Marketplace] Total buckets:', totalBuckets, 'Available:', availableBuckets);

      setBuckets(marketplaceBuckets);
      setStats({
        totalBuckets,
        activeProviders: new Set(marketplaceBuckets.map(b => b.provider_id)).size,
        avgReputation: 4.7,
        availableBuckets,
      });
    } catch (error) {
      console.error('Failed to load marketplace buckets:', error);
      setBuckets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isConnected && network) {
      loadMarketplaceBuckets();
    }
  }, [isAuthenticated, isConnected, network, selectedWorkspaceId, stellarClient, suiClient]);

  // Filtered buckets based on search
  const filteredBuckets = buckets.filter((bucket) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      bucket.name.toLowerCase().includes(query) ||
      bucket.region.toLowerCase().includes(query) ||
      bucket.provider_id.toLowerCase().includes(query)
    );
  });

  // Format helpers
  const formatPrice = (price: bigint) => {
    const cents = Number(price) / 100000; // Convert to cents
    return `${cents.toFixed(1)}¢`;
  };

  const formatUptime = (uptime: number) => {
    const percentage = (uptime / 1000000).toFixed(2);
    return `${percentage}%`;
  };

  // Handle purchase/create deal
  const handlePurchase = (bucket: MarketplaceBucket) => {
    setSelectedBucket(bucket);
    setCreateDealModalOpen(true);
  };

  // Render wallet connection UI if not connected
  if (!isConnected && network) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
        <DePINHeader
          title="ISP Market"
          subtitle="Sell, purchase, and manage your resources (storage units ...) provided by Infrastructure Service Providers in the marketplace of your choice."
          sectionTitle="Marketplace"
          sectionSubtitle="Discover and purchase DePIN resources from community providers"
          network={network}
          isConnected={false}
          showAddButton={false}
        />

        <div className="flex-1 px-10 pb-6 flex items-center justify-center">
          <div style={{ maxWidth: 420, width: '100%', padding: '40px 32px', textAlign: 'center', backgroundColor: '#FFFFFF', borderRadius: 16, border: '1px solid #E9EAEB', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <div style={{ width: 56, height: 56, margin: '0 auto 20px', borderRadius: 14, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#181D27', marginBottom: 8 }}>Connect Your Wallet</h3>
            <p style={{ fontSize: 13, color: '#717680', marginBottom: 24, lineHeight: '20px' }}>
              You need to connect your {network === 'stellar' ? 'Stellar' : 'SUI'} wallet to browse the marketplace.
            </p>

            {network === 'stellar' ? (
              <button
                onClick={() => connectWallet(WalletType.STELLAR)}
                disabled={walletLoading}
                style={{ padding: '10px 24px', backgroundColor: '#617BFF', color: '#FFFFFF', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background-color 0.2s' }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#5A6FE8')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#617BFF')}
              >
                {walletLoading ? 'Connecting...' : 'Connect Wallet'}
              </button>
            ) : (
              <div className="flex justify-center">
                <ConnectButton />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render no network selected state
  if (!network) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
        <DePINHeader
          title="ISP Market"
          subtitle="Sell, purchase, and manage your resources (storage units ...) provided by Infrastructure Service Providers in the marketplace of your choice."
          sectionTitle="Marketplace"
          sectionSubtitle="Discover and purchase DePIN resources from community providers"
          network={null}
          isConnected={false}
          showAddButton={false}
        />

        <div className="flex-1 px-10 pb-6 flex items-center justify-center">
          <div style={{ maxWidth: 420, width: '100%', padding: '40px 32px', textAlign: 'center', backgroundColor: '#FFFFFF', borderRadius: 16, border: '1px solid #E9EAEB', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <div style={{ width: 56, height: 56, margin: '0 auto 20px', borderRadius: 14, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#181D27', marginBottom: 8 }}>Select a Network</h3>
            <p style={{ fontSize: 13, color: '#717680', lineHeight: '20px' }}>
              Please select a blockchain network (Stellar or SUI) from the header menu to continue.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* DePIN Hub Tutorial */}
      <DePINHubTutorialLauncher
        isVisible={showTutorial}
        onDismiss={() => { /* Tutorial always visible */ }}
        stepsStatus={[
          { label: 'Connect your wallet', completed: isConnected },
          { label: 'Register a bucket', completed: false },
          { label: 'Browse the marketplace', completed: true },
          { label: 'Create your first deal', completed: false },
        ]}
        currentStep={2}
      />

      <DePINHeader
        title="ISP Market"
        subtitle="Sell, purchase, and manage your resources (storage units ...) provided by Infrastructure Service Providers in the Stellar marketplace."
        sectionTitle="Marketplace"
        sectionSubtitle="Discover and purchase DePIN resources from community providers"
        network={network}
        isConnected={isConnected}
        balances={getBalances()}
        loading={loading}
        onRefresh={loadMarketplaceBuckets}
        showAddButton={false}
        showFilterButton={true}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search marketplace..."
      />

      {/* Content */}
      <div className="flex-1 px-10 pb-6 overflow-y-auto">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Buckets */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-3">
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  backgroundColor: '#F0F2FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
              </div>
              <div className="flex-1">
                <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Total Storage Units</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#181D27' }}>{stats.totalBuckets.toLocaleString()}</div>
              </div>
            </div>
          </div>

          {/* Active Providers */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-3">
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  backgroundColor: '#EAF8F1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="flex-1">
                <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Active Providers</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#181D27' }}>{stats.activeProviders}</div>
              </div>
            </div>
          </div>

          {/* AVG Reputation */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-3">
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  backgroundColor: '#FFF7E6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </div>
              <div className="flex-1">
                <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>AVG Reputation</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#181D27' }}>{stats.avgReputation.toFixed(1)}</div>
              </div>
            </div>
          </div>

          {/* Available Buckets */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-3">
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  backgroundColor: '#F0F9FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#06B6D4" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <div className="flex-1">
                <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Available Storage Units</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#181D27' }}>{stats.availableBuckets}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Marketplace Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#617BFF] mr-3"></div>
            <span className="text-gray-500">Loading marketplace...</span>
          </div>
        ) : filteredBuckets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Typography variant="h6" style={{ color: '#9CA3AF', fontWeight: 500, marginBottom: '8px' }}>
              No items found
            </Typography>
            <Typography variant="body2" style={{ color: '#9CA3AF' }}>
              {searchQuery ? 'Try adjusting your search or filters' : 'No storage units available in the marketplace yet'}
            </Typography>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredBuckets.map((bucket) => (
              <div
                key={bucket.bucket_id}
                className="border border-gray-200 rounded-lg p-5 bg-white hover:shadow-md transition-shadow flex flex-col"
              >
                {/* Header Section */}
                <div className="mb-4 pb-4 border-b border-gray-100">
                  <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#181D27', marginBottom: '8px' }}>
                    {bucket.name}
                  </h3>

                  <div className="flex items-start gap-3">
                    {/* Region Logo */}
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        backgroundColor: '#F0F2FF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M2 12h20" />
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                      </svg>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#181D27' }}>
                          {bucket.region}
                        </span>
                        {bucket.country && (
                          <span style={{ fontSize: '11px', color: '#9CA3AF' }}>
                            ({bucket.country})
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', fontWeight: 500, color: '#6B7280' }}>
                        Provider: {bucket.provider_id?.slice(0, 8)}...{bucket.provider_id?.slice(-4)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pricing Section */}
                <div className="mb-4">
                  <h4 style={{ fontSize: '12px', fontWeight: 600, color: '#181D27', marginBottom: '10px' }}>
                    Pricing
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Storage</div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#414651' }}>
                        {formatPrice(bucket.price_per_gb_storage)}/GB
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Egress</div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#414651' }}>
                        {formatPrice(bucket.price_per_gb_egress)}/GB
                      </div>
                    </div>
                  </div>
                </div>

                {/* Features Section */}
                <div className="mb-4 flex-1">
                  <h4 style={{ fontSize: '12px', fontWeight: 600, color: '#181D27', marginBottom: '10px' }}>
                    Features
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Best Latency</div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#10B981' }}>
                        {bucket.sla_avg_latency_ms}ms
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>Uptime</div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#10B981' }}>
                        {formatUptime(bucket.sla_avg_uptime_pct)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* API Compatibility */}
                <div className="mb-4">
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>API Compatibility</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#414651' }}>
                    {bucket.api_compatibility}
                  </div>
                </div>

                {/* Action Button */}
                <div className="pt-3 border-t border-gray-100">
                  <button
                    className="w-full px-3 py-2 rounded-lg hover:opacity-90 transition-colors"
                    style={{ backgroundColor: '#617BFF', fontWeight: 600, fontSize: 13, color: 'white' }}
                    onClick={() => handlePurchase(bucket)}
                  >
                    Purchase Storage
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Help Section */}
        <Box
          sx={{
            mt: 6,
            p: 4,
            borderRadius: '12px',
            border: '1px solid #E9EAEB',
            backgroundColor: '#FAFAFA',
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '16px', lineHeight: '24px', color: '#181D27', mb: 2 }}>
            Help and Support
          </Typography>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="https://docs.flashback.network/marketplace/overview"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-[#617BFF] transition-colors bg-white"
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  backgroundColor: '#F0F2FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </div>
              <div>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#181D27', mb: 0.5 }}>
                  What's the Marketplace?
                </Typography>
                <Typography sx={{ fontWeight: 400, fontSize: 13, color: '#717680' }}>
                  Learn about DePIN marketplace
                </Typography>
              </div>
            </a>

            <a
              href="https://docs.flashback.network/marketplace/buying"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-[#617BFF] transition-colors bg-white"
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  backgroundColor: '#F0F2FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <div>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#181D27', mb: 0.5 }}>
                  How to purchase resources
                </Typography>
                <Typography sx={{ fontWeight: 400, fontSize: 13, color: '#717680' }}>
                  Follow the buying guide
                </Typography>
              </div>
            </a>

            <a
              href="https://docs.flashback.network/api-reference/marketplace"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-[#617BFF] transition-colors bg-white"
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  backgroundColor: '#F0F2FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                  <polyline points="16 18 22 12 16 6" />
                  <polyline points="8 6 2 12 8 18" />
                </svg>
              </div>
              <div>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#181D27', mb: 0.5 }}>
                  Build with API
                </Typography>
                <Typography sx={{ fontWeight: 400, fontSize: 13, color: '#717680' }}>
                  Explore marketplace API
                </Typography>
              </div>
            </a>
          </div>
        </Box>
      </div>

      {/* Create Deal Modal */}
      <CreateDealModal
        open={createDealModalOpen}
        onClose={() => {
          setCreateDealModalOpen(false);
          setSelectedBucket(null);
        }}
        bucket={selectedBucket}
        network={network}
        suiClient={suiClient}
        onDealCreated={() => {
          navigate('/depin-hub/deals');
        }}
      />
    </div>
  );
}

export default DePINHubMarketplace;

